import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Lock, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Forced password reset for divisional supervisors on first login when JWT
 * carries must_set_password=true. Non-dismissible — blocks the dashboard
 * until they pick their own password (or log out).
 */
export default function SupervisorPasswordResetModal({ divisionName, onComplete, onLogout }) {
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const a = (pw1 || "").trim();
    const b = (pw2 || "").trim();
    if (a.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (a.length > 64) { toast.error("Password must be 64 characters or fewer"); return; }
    if (/\s/.test(a)) { toast.error("Password cannot contain spaces"); return; }
    if (a !== b) { toast.error("Passwords do not match"); return; }
    setSaving(true);
    try {
      const tk = localStorage.getItem("token");
      const res = await axios.post(`${API}/supervisor/set-password`, { new_password: a }, {
        headers: { Authorization: `Bearer ${tk}` },
      });
      const newToken = res.data?.token;
      if (newToken) {
        localStorage.setItem("token", newToken);
        axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
      }
      toast.success("Password set — you're in!");
      onComplete?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to set password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={() => { /* non-dismissible */ }}>
      <DialogContent
        className="max-w-md"
        data-testid="supervisor-password-reset-modal"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">
            <Lock className="w-5 h-5" /> Set Your Supervisor Password
          </DialogTitle>
          <DialogDescription>
            <strong>{divisionName}</strong> — for security, each divisional supervisor must use a unique
            password. The shared admin password will <strong>stop working</strong> for your division as soon
            as you save below.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          <div>
            <Label htmlFor="new-sup-pw" className="text-xs font-semibold">New password (min 6 characters)</Label>
            <div className="relative mt-1">
              <Input
                id="new-sup-pw"
                type={show ? "text" : "password"}
                value={pw1}
                onChange={(e) => setPw1(e.target.value)}
                placeholder="At least 6 characters, no spaces"
                autoFocus
                data-testid="sup-pw-new"
              />
              <button
                type="button"
                onClick={() => setShow(v => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                aria-label={show ? "Hide password" : "Show password"}
              >
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <Label htmlFor="confirm-sup-pw" className="text-xs font-semibold">Confirm new password</Label>
            <Input
              id="confirm-sup-pw"
              type={show ? "text" : "password"}
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              className="mt-1"
              data-testid="sup-pw-confirm"
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
            />
          </div>

          <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 text-[11px] text-amber-900">
            Your admin can view (behind a reveal button) and reset your password if you forget. Pick
            something memorable but unique.
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t">
          <Button variant="ghost" size="sm" onClick={onLogout} data-testid="sup-pw-logout" className="text-slate-600">
            Log out instead
          </Button>
          <Button
            onClick={submit}
            disabled={saving || !pw1 || !pw2}
            className="bg-rose-600 hover:bg-rose-700 text-white"
            data-testid="sup-pw-save"
          >
            {saving ? "Saving..." : "Save Password"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
